import { MIS_3005_D_SIZING, RULES_BUNDLE_VERSION } from "@sts/rules-data";
import { ComplianceError, ENGINE_VERSION } from "./heat-loss.js";
import type { CalcStamp, CalcWarning } from "./types.js";

export type SystemKind = "monovalent" | "hybrid" | "air-to-air";

export interface SizingInput {
  /** Design heat load from the room-by-room calculation, W. */
  readonly designHeatLoadW: number;
  /**
   * Heat pump heating capacity at the DESIGN FLOW TEMPERATURE and the DESIGN
   * EXTERNAL TEMPERATURE, W — excluding any supplementary electric heater.
   * MIS 3005-D §3.4.1(e). Source this from the MCS Product Directory.
   */
  readonly heatPumpCapacityAtDesignW: number;
  readonly designFlowTempC: number;
  readonly systemKind: SystemKind;
  /**
   * Hybrid only: heat pump capacity rated at 55°C flow (from the MCS database)
   * at the design external temperature, W. MIS 3005-D §3.4.2.
   */
  readonly hybridHeatPumpCapacityAt55W?: number;
  /** Hybrid only: capacity of the supplementary (e.g. gas) appliance, W. */
  readonly hybridSupplementaryCapacityW?: number;
  readonly complianceMode: boolean;
  readonly calculationDate: string;
}

export interface SizingResult {
  readonly meetsCapacityRule: boolean;
  /** Capacity as a fraction of the design heat load (1.0 = exactly 100%). */
  readonly capacityFraction: number;
  readonly requiresAlternativeLowerFlowTempDesign: boolean;
  readonly warnings: readonly CalcWarning[];
  readonly stamp: CalcStamp;
}

/**
 * MIS 3005-D §3.4 sizing checks.
 *
 * The core rule (§3.4.1(e)): the heat pump must provide AT LEAST 100% of the
 * calculated heat load at the design flow temperature, "without input from any
 * supplementary electric heater". The standard sets a floor, not a ceiling —
 * there is no numeric oversize cap, though oversizing is discouraged.
 */
export function checkSizing(input: SizingInput): SizingResult {
  const rules = MIS_3005_D_SIZING.data;
  const warnings: CalcWarning[] = [];

  if (input.designHeatLoadW <= 0) {
    warnings.push({
      code: "INVALID_HEAT_LOAD",
      severity: "compliance-failure",
      message: "Design heat load must be greater than zero to size a heat pump.",
    });
  }

  const capacityFraction =
    input.designHeatLoadW > 0
      ? input.heatPumpCapacityAtDesignW / input.designHeatLoadW
      : 0;

  /**
   * The ≥100% rule of §3.4.1(e) governs monovalent systems, and §3.4.3(d) the
   * air-to-air outdoor unit. Hybrids are governed instead by §3.4.2 (combined
   * output ≥100%, heat pump alone ≥55% rated at 55°C), so the heat pump on its
   * own is NOT required to cover the full load.
   */
  const monovalentRuleApplies = input.systemKind !== "hybrid";
  const meetsCapacityRule = monovalentRuleApplies
    ? capacityFraction >= rules.minCapacityFraction
    : true; // hybrid adequacy is decided by the §3.4.2 checks below

  if (monovalentRuleApplies && !meetsCapacityRule) {
    warnings.push({
      code: "MIS3005D_UNDERSIZED",
      severity: "compliance-failure",
      message:
        `Heat pump provides ${(capacityFraction * 100).toFixed(1)}% of the ${input.designHeatLoadW} W ` +
        `design heat load at ${input.designFlowTempC}°C flow. MIS 3005-D §3.4.1(e) requires at least ` +
        `${(rules.minCapacityFraction * 100).toFixed(0)}%, with no contribution from supplementary ` +
        `electric heaters.`,
    });
  }

  if (input.systemKind === "air-to-air") {
    warnings.push({
      code: "AIR_TO_AIR_PER_ROOM_CHECKS_NOT_COVERED",
      severity: "info",
      message:
        "This check covers the outdoor unit only (§3.4.3(d)). MIS 3005-D §3.4.3(f)-(h) additionally " +
        "require total indoor capacity ≥100% of the building load, each indoor unit ≥100% of its own " +
        "room's load, and an indoor unit in every habitable room — assess those per room.",
    });
  }

  // §3.4.4 / §3.4.5 — designs above 55°C must be offered alongside a ≤55°C alternative.
  const requiresAlternativeLowerFlowTempDesign =
    input.designFlowTempC > rules.alternativeDesignFlowTempThresholdC;

  if (requiresAlternativeLowerFlowTempDesign) {
    warnings.push({
      code: "MIS3005D_ALTERNATIVE_DESIGN_REQUIRED",
      severity: "warning",
      message:
        `Design flow temperature is ${input.designFlowTempC}°C. MIS 3005-D §3.4.5 requires an ` +
        `alternative design at ${rules.alternativeDesignFlowTempThresholdC}°C or lower to be provided, ` +
        `with the efficiency and energy differences explained to the customer.`,
    });
  }

  if (input.systemKind === "hybrid") {
    const hpAt55 = input.hybridHeatPumpCapacityAt55W;
    const supplementary = input.hybridSupplementaryCapacityW ?? 0;

    if (hpAt55 === undefined) {
      warnings.push({
        code: "HYBRID_MISSING_55C_CAPACITY",
        severity: "compliance-failure",
        message:
          "Hybrid systems need the heat pump capacity rated at 55°C flow (MCS database) to check " +
          "MIS 3005-D §3.4.2.",
      });
    } else {
      const hybridFraction =
        input.designHeatLoadW > 0 ? hpAt55 / input.designHeatLoadW : 0;
      if (hybridFraction < rules.hybridMinHeatPumpFraction) {
        warnings.push({
          code: "MIS3005D_HYBRID_HP_SHARE_TOO_LOW",
          severity: "compliance-failure",
          message:
            `Hybrid heat pump provides ${(hybridFraction * 100).toFixed(1)}% of the heat load rated at ` +
            `${rules.hybridRatingFlowTempC}°C. MIS 3005-D §3.4.2 requires at least ` +
            `${(rules.hybridMinHeatPumpFraction * 100).toFixed(0)}%.`,
        });
      }

      const combined = hpAt55 + supplementary;
      if (combined < input.designHeatLoadW) {
        warnings.push({
          code: "MIS3005D_HYBRID_COMBINED_TOO_LOW",
          severity: "compliance-failure",
          message:
            `Combined hybrid output (${combined} W) is below the design heat load ` +
            `(${input.designHeatLoadW} W). MIS 3005-D §3.4.2 requires at least 100% combined.`,
        });
      }
    }
  }

  if (input.complianceMode) {
    const failures = warnings.filter((w) => w.severity === "compliance-failure");
    if (failures.length > 0) {
      throw new ComplianceError("Sizing check failed in compliance mode.", failures);
    }
  }

  return {
    meetsCapacityRule,
    capacityFraction: Math.round(capacityFraction * 1e6) / 1e6,
    requiresAlternativeLowerFlowTempDesign,
    warnings,
    stamp: {
      engineVersion: ENGINE_VERSION,
      rulesBundleVersion: RULES_BUNDLE_VERSION,
      calculatedAt: input.calculationDate,
      complianceMode: input.complianceMode,
    },
  };
}
